// packages/map/src/configSpec.ts
import { invalid } from "@opendfieldmap/core";
var OEM_MAP_CONFIG_FIELDS = Object.freeze({
  region: { url: ["r"], group: "view", kind: "string", state: "regionId" },
  subregion: { url: ["s"], group: "view", kind: "nullableString", state: "subregionId", nullable: true },
  floor: { url: ["layer"], group: "view", kind: "string", state: "floorId" },
  center: { url: ["cx", "cz", "centerZ"], group: "view", kind: "center", state: "center" },
  zoom: { url: ["z"], group: "view", kind: "number", state: "zoom" },
  locale: { url: ["l"], group: "layers", kind: "string", state: "locale" },
  markerTypes: { url: ["f"], group: "layers", kind: "markers", state: "markerTypes" },
  labels: { url: ["labels", "names"], group: "layers", kind: "boolean", state: "labels" },
  boundaries: { url: ["boundaries", "boundary"], group: "layers", kind: "boolean", state: "boundaries" },
  boundarySource: {
    url: ["boundarySource"],
    group: "layers",
    kind: "boundarySource",
    state: "boundarySource"
  },
  markerClustering: { url: ["cluster"], group: "layers", kind: "boolean", state: "markerClustering" },
  lockDrag: { url: ["lockDrag"], group: "interaction", kind: "boolean", state: "lockDrag" },
  lockZoom: { url: ["lockZoom"], group: "interaction", kind: "boolean", state: "lockZoom" },
  theme: { url: ["theme"], group: "appearance", kind: "theme", state: "theme" },
  customPoints: { group: "points", kind: "points" },
  customPointsUrl: { group: "points", kind: "string" }
});
var OEM_MAP_DEFAULTS = Object.freeze({
  labels: true,
  boundaries: false,
  boundarySource: "oem",
  markerClustering: true,
  lockDrag: false,
  lockZoom: false,
  theme: "light"
});
var OEM_MAP_CONFIG_KEYS = Object.freeze(
  Object.keys(OEM_MAP_CONFIG_FIELDS)
);
function flattenOEMMapConfig(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) invalid("options", "Expected an object");
  const result = {};
  for (const group of ["view", "layers", "interaction"]) {
    const values = input[group];
    if (values === void 0) continue;
    if (!values || typeof values !== "object" || Array.isArray(values))
      invalid(`options.${group}`, "Expected an object");
    for (const [key, value] of Object.entries(values)) {
      if (!(key in OEM_MAP_CONFIG_FIELDS) || OEM_MAP_CONFIG_FIELDS[key].group !== group)
        invalid(`options.${group}.${key}`, "Unknown field in this group");
      if (value !== void 0) result[key] = value;
    }
  }
  for (const key of OEM_MAP_CONFIG_KEYS) if (input[key] !== void 0) result[key] = input[key];
  for (const key of OEM_MAP_CONFIG_KEYS)
    if (result[key] === null && key !== "subregion")
      invalid(`options.${key}`, "This field cannot be cleared with null");
  return result;
}
function equalOEMConfigValue(a, b) {
  if (Object.is(a, b)) return true;
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const left = Object.keys(a), right = Object.keys(b);
  return left.length === right.length && left.every(
    (key) => Object.hasOwn(b, key) && equalOEMConfigValue(a[key], b[key])
  );
}
function diffOEMMapConfig(previous, next) {
  const before = flattenOEMMapConfig(previous), after = flattenOEMMapConfig(next);
  return Object.fromEntries(
    OEM_MAP_CONFIG_KEYS.filter(
      (key) => after[key] !== void 0 && !equalOEMConfigValue(before[key], after[key])
    ).map((key) => [key, after[key]])
  );
}

// packages/map/src/config.ts
import { getOEMRegion, getOEMDefaultFloor, normalizeOEMLocale, invalid as invalid2 } from "@opendfieldmap/core";
var OEM_REGION_ALIASES = Object.freeze({
  VL: "Valley_4",
  WL: "Wuling",
  DJ: "Dijiang",
  ES: "Weekraid_1"
});
var validateConfig = (config) => {
  config = flattenOEMMapConfig(config);
  for (const key of OEM_MAP_CONFIG_KEYS) {
    const value = config[key];
    if (value === void 0) continue;
    const path = `options.${key}`;
    switch (OEM_MAP_CONFIG_FIELDS[key].kind) {
      case "string":
        if (typeof value !== "string" || !value.trim()) invalid2(path, "Expected a non-empty string");
        break;
      case "nullableString":
        if (value !== null && typeof value !== "string") invalid2(path, "Expected a string or null");
        break;
      case "boolean":
        if (typeof value !== "boolean") invalid2(path, "Expected boolean");
        break;
      case "number":
        if (!Number.isFinite(value)) invalid2(path, "Expected a finite number");
        break;
      case "theme":
        if (value !== "light" && value !== "dark") invalid2(path, "Unknown official theme");
        break;
      case "boundarySource":
        if (value !== "oem" && value !== "game") invalid2(path, "Expected oem or game");
        break;
      case "center": {
        const center = value;
        if (!center || !Number.isFinite(center.x) || !Number.isFinite(center.z))
          invalid2(path, "Center coordinates must be finite");
        if (center.space !== void 0 && center.space !== "pixel")
          invalid2(`${path}.space`, "Center uses published pixels");
        break;
      }
      case "markers":
        if (value !== false && value !== "*" && (!Array.isArray(value) || value.some((item) => typeof item !== "string")))
          invalid2(path, "Expected type keys, * or false");
        break;
      case "points": {
        if (!Array.isArray(value)) invalid2(path, "Expected an array");
        value.forEach((point, index) => {
          if (!point || typeof point !== "object" || !point.position || typeof point.position !== "object")
            invalid2(`${path}[${index}]`, "Expected a point with a position");
        });
        break;
      }
    }
  }
  if (config.customPoints !== void 0 && config.customPointsUrl !== void 0)
    invalid2("options.customPoints", "Pass either customPoints or customPointsUrl, not both");
};
var cloneConfig = (input) => {
  const config = flattenOEMMapConfig(input);
  return {
    ...config,
    markerTypes: Array.isArray(config.markerTypes) ? [...config.markerTypes] : config.markerTypes,
    customPoints: config.customPoints?.map((point) => ({ ...point, position: { ...point.position } })),
    center: config.center ? { ...config.center } : config.center
  };
};
var resolveRegionId = (manifest, value) => {
  const requested = value ? manifest.regions.some((region) => region.id === value) ? value : OEM_REGION_ALIASES[value.toUpperCase()] ?? value : manifest.defaultRegionId;
  return getOEMRegion(manifest, requested).id;
};
var normalizeMarkerTypes = (value) => {
  if (value === "*") return "*";
  if (!value || !Array.isArray(value)) return [];
  return [...new Set(value.map((entry) => entry.trim()).filter(Boolean))];
};
var normalizeBoundarySource = (value) => {
  if (value === void 0 || value === "oem") return "oem";
  if (value === "game") return "game";
  throw new Error(`Unknown OEM boundary source: ${value}`);
};
var getPreset = (region, subregion) => {
  if (!subregion?.bounds) {
    return {
      center: { x: region.initialView.x, z: region.initialView.z },
      zoom: region.initialView.zoom
    };
  }
  return {
    center: {
      x: (subregion.bounds[0][0] + subregion.bounds[1][0]) / 2,
      z: (subregion.bounds[0][1] + subregion.bounds[1][1]) / 2
    },
    zoom: Math.max(region.minZoom, Math.min(region.maxZoom, 1))
  };
};
var normalizeState = (input, manifest) => {
  input = flattenOEMMapConfig(input);
  validateConfig(input);
  let regionId = resolveRegionId(manifest, input.region);
  const subregionId = input.subregion?.trim() || null;
  if (subregionId) {
    const owner = manifest.regions.find(
      (region2) => region2.subregions.some((subregion2) => subregion2.id === subregionId)
    );
    if (!owner) throw new Error(`Unknown OEM subregion: ${subregionId}`);
    if (input.region && owner.id !== regionId) {
      throw new Error(`OEM subregion ${subregionId} does not belong to ${regionId}`);
    }
    regionId = owner.id;
  }
  const region = getOEMRegion(manifest, regionId);
  const subregion = subregionId ? region.subregions.find((entry) => entry.id === subregionId) : void 0;
  const floorId = input.floor ?? getOEMDefaultFloor(region);
  if (!region.floors.some((floor) => floor.id === floorId)) {
    throw new Error(`Unknown OEM floor ${floorId} in ${regionId}`);
  }
  const requestedCenter = input.center;
  if (requestedCenter && (!Number.isFinite(requestedCenter.x) || !Number.isFinite(requestedCenter.z))) {
    throw new Error("OEM Widget center coordinates must be finite");
  }
  const preset = getPreset(region, subregion);
  const requestedLocale = input.locale ?? (typeof navigator === "undefined" ? manifest.fallbackLocale : navigator.language);
  return {
    regionId,
    subregionId,
    floorId,
    locale: normalizeOEMLocale(
      requestedLocale,
      Object.keys(manifest.locales),
      manifest.fallbackLocale
    ),
    markerTypes: normalizeMarkerTypes(input.markerTypes),
    labels: input.labels ?? OEM_MAP_DEFAULTS.labels,
    boundaries: input.boundaries ?? OEM_MAP_DEFAULTS.boundaries,
    boundarySource: normalizeBoundarySource(input.boundarySource),
    markerClustering: input.markerClustering ?? OEM_MAP_DEFAULTS.markerClustering,
    theme: input.theme ?? OEM_MAP_DEFAULTS.theme,
    lockDrag: input.lockDrag ?? OEM_MAP_DEFAULTS.lockDrag,
    lockZoom: input.lockZoom ?? OEM_MAP_DEFAULTS.lockZoom,
    zoom: Math.max(region.minZoom, Math.min(region.maxZoom, input.zoom ?? preset.zoom)),
    center: {
      x: requestedCenter?.x ?? preset.center.x,
      z: requestedCenter?.z ?? preset.center.z
    }
  };
};
var toConfig = (state) => Object.fromEntries(
  OEM_MAP_CONFIG_KEYS.flatMap((key) => {
    const field = OEM_MAP_CONFIG_FIELDS[key];
    return "state" in field ? [[key, state[field.state]]] : [];
  })
);
var sameState = (left, right) => OEM_MAP_CONFIG_KEYS.every((key) => {
  const field = OEM_MAP_CONFIG_FIELDS[key];
  return !("state" in field) || equalOEMConfigValue(left[field.state], right[field.state]);
});
var hasMarkers = (state) => state.markerTypes === "*" || state.markerTypes.length > 0;
function resolveConfigUpdate(previous, config, manifest) {
  const changes = Object.fromEntries(
    Object.entries(config).filter(([, value]) => value !== void 0)
  );
  if (changes.subregion !== void 0) changes.subregion = changes.subregion?.trim() || null;
  if (changes.subregion && changes.region === void 0) {
    const owner = manifest.regions.find(
      (region) => region.subregions.some((sub) => sub.id === changes.subregion)
    );
    if (!owner) invalid2("options.subregion", "Unknown subregion");
    changes.region = owner.id;
  }
  const merged = { ...toConfig(previous), ...changes };
  const regionId = resolveRegionId(manifest, changes.region ?? previous.regionId);
  if (regionId !== previous.regionId) {
    const preset = getPreset(getOEMRegion(manifest, regionId));
    if (changes.floor === void 0) merged.floor = void 0;
    if (changes.subregion === void 0) merged.subregion = null;
    if (changes.center === void 0) merged.center = preset.center;
    if (changes.zoom === void 0) merged.zoom = preset.zoom;
  }
  if (changes.subregion !== void 0 && changes.subregion !== previous.subregionId) {
    const region = getOEMRegion(manifest, regionId);
    const preset = getPreset(
      region,
      region.subregions.find((sub) => sub.id === changes.subregion)
    );
    if (changes.center === void 0) merged.center = preset.center;
    if (changes.zoom === void 0) merged.zoom = preset.zoom;
  }
  const next = normalizeState(merged, manifest);
  return { changes, next };
}

export {
  OEM_MAP_CONFIG_FIELDS,
  OEM_MAP_DEFAULTS,
  OEM_MAP_CONFIG_KEYS,
  flattenOEMMapConfig,
  equalOEMConfigValue,
  diffOEMMapConfig,
  OEM_REGION_ALIASES,
  validateConfig,
  cloneConfig,
  normalizeState,
  sameState,
  hasMarkers,
  resolveConfigUpdate
};
//# sourceMappingURL=chunk-XKKVLVST.js.map
